import { assign, createMachine } from 'xstate';

import { INVITE_CODE } from '@nw/shared/config';
import { getAPIErrorEnum, getURLSearchParam } from '@nw/shared/utils';

import {
  FORMS_MACHINE_ID,
  formsMachineActions,
  formsMachineEvents,
  formsMachineStates,
  formsMachineTags
} from '../../constants/formMachineConstants';

import { FormsMachineContext, FormsMachineEvent } from './types';

const {
  getInitialConfigState,
  getInitialConfigFailureState,
  notLoggedInUserState,
  getClientAuthCodeState,
  getClientAuthenticationDetailsState,
  getProfileDetailsState,
  submitForm,
  getCollegePredictorClientAuthCodeState,
  getCollegePredictorClientAuthenticationDetailsState,
  updateProfileState,
  updateGraphQlFieldsState,
  submitPublicForm,
  loggedInUserState
} = formsMachineStates;

const {
  getInitialConfigRetryEvent,
  goToOtpVerificationStep,
  onSuccessOtpVerification,
  goToNotLoggedInUserState,
  goToPreviousStep,
  clearErrorMessage,
  goToNextStep,
  loggingOutUser,
  goToSubmitFormState,
  assignConfigEventsToContext,
  callEventsFromConfig,
  doSelfTransition,
  sendSuccessEventToDataLayer,
  sendPreRegistrationEventToDataLayer,
  loggedOutUser,
  setGoToNextSteps,
  goToGetCollegePredictorClientAuthCodeState,
  goToUpdateProfileDetailsState,
  goToUpdateGraphQlFieldsState,
  spawnPaginationMachine,
  sendFormSubmitSegmentEvent,
  sendRegistrationSuccessSegmentEvent,
  sendUserVerifiedOtpSegmentEvent,
  sendIdentifySegmentEvent,
  goToSubmitPublicForm,
  getIbFormSubmissionDetails,
  updateSelectedTab,
  resendOtpClicked,
  sendPostMessage
} = formsMachineEvents;

const {
  getInitialConfigLoadingTag,
  otpVerifyBtnLoading,
  buttonLoading,
  formLoading
} = formsMachineTags;

const events = formsMachineEvents;
const actions = formsMachineActions;
const states = formsMachineStates;

//TODO: After finalizing segment flow , setup values in context
export const publicFormsMachine = (formId: string) =>
  createMachine<FormsMachineContext, FormsMachineEvent>({
    id: FORMS_MACHINE_ID,
    initial: loggingOutUser,
    context: {
      formInfo: null,
      shouldShowGetOTP: false,
      next: () => { },
      back: () => { },
      eventsFromConfig: [],
      goToNextSteps: [],
      otpFieldRef: null,
      isOtpSentToUser: false,
      isLoggedIn: false,
      values: null,
      inviteCode: getURLSearchParam().has(INVITE_CODE)
        ? (getURLSearchParam().get(INVITE_CODE) as string)
        : '',
      userProfileDetails: null,
      orderDetails: null,
      isNewUser: false,
      authCode: '',
      referralCodes: null,
      isInviteCodePresentInBackend: false,
      errorMessage: '',
      isOtpVerified: false,
      isNextAndSubmitBtnLoading: false,
      formSubmittedValues: {},
      collegePredictorAuthCode: '',
      paginationMachineRef: null,
      eachPageLimit: 10,
      authMachineRef: null,
      isOTPButtonLoading: false,
      formattedRequestObjectKeys: null,
      formSubmissionDetails: null,
      collegePredictorTabsInfo: null,
      selectedTab: null,
      formId: formId
    },
    on: {
      [goToPreviousStep]: {
        actions: ['callBackFromContext']
      },

      [goToNextStep]: {
        actions: ['callNextFromContext']
      },

      [clearErrorMessage]: {
        actions: ['clearErrorMessageFromContext', 'callEventsFromConfig']
      },

      [goToNotLoggedInUserState]: { target: notLoggedInUserState },

      [assignConfigEventsToContext]: {
        actions: 'assignConfigEventsToContext'
      },
      [goToSubmitFormState]: {
        target: submitForm
      },

      [callEventsFromConfig]: {
        actions: 'callEventsFromConfig'
      },
      [resendOtpClicked]: {
        actions: [actions.sendResendOtpEventToDataLayer]
      },
      [sendSuccessEventToDataLayer]: {
        actions: ['sendSuccessEventToDataLayer', 'callEventsFromConfig']
      },
      [sendPreRegistrationEventToDataLayer]: {
        actions: ['sendPreRegistrationEventToDataLayer', 'callEventsFromConfig']
      },
      [loggedOutUser]: {
        target: loggingOutUser
      },
      [onSuccessOtpVerification]: {
        actions: [actions.setLoginAccessAndRefreshToken],
        target: getProfileDetailsState
      },
      [setGoToNextSteps]: {
        actions: ['setGoToNextSteps', 'callEventsFromConfig']
      },
      [goToGetCollegePredictorClientAuthCodeState]: {
        target: getCollegePredictorClientAuthCodeState
      },
      [goToUpdateProfileDetailsState]: {
        target: updateProfileState
      },
      [goToUpdateGraphQlFieldsState]: {
        target: updateGraphQlFieldsState
      },
      [updateSelectedTab]: {
        actions: [actions.updateSelectedTab]
      },
      [spawnPaginationMachine]: {
        actions: [
          actions.setCollegePredictorTabsContext,
          actions.updateSelectedTab,
          'spawnPaginationMachine',
          'callEventsFromConfig',
          'setNextAndSubmitBtnLoadingToFalse'
        ]
      },
      [sendFormSubmitSegmentEvent]: {
        actions: ['sendFormSubmitSegmentEvent', 'callEventsFromConfig']
      },
      [sendRegistrationSuccessSegmentEvent]: {
        actions: ['sendRegistrationSuccessSegmentEvent', 'callEventsFromConfig']
      },
      [sendUserVerifiedOtpSegmentEvent]: {
        actions: ['sendUserVerifiedOtpSegmentEvent', 'callEventsFromConfig']
      },
      [sendIdentifySegmentEvent]: {
        actions: ['sendIdentifySegmentEvent', 'callEventsFromConfig']
      },
      [goToSubmitPublicForm]: {
        target: submitPublicForm
      },
      [events.onEntryGetOTP]: {
        actions: [
          actions.setIsOtpButtonLoadingToTrue,
          assign((context: FormsMachineContext) => {
            if (!context.shouldShowGetOTP)
              return {
                isNextAndSubmitBtnLoading: true
              };
            else return {};
          }),
          actions.clearErrorMessageFromContext,
          actions.resetOtp,
          actions.updateFormValuesToContext
        ]
      },
      [events.onSuccessGetOTP]: {
        actions: [
          actions.setIsOtpButtonLoadingToFalse,
          actions.setNextAndSubmitBtnLoadingToFalse,
          actions.updateFormValuesToContext,
          actions.updateIsOtpSentToUserInContext,
          actions.sendGetOtpCustomEventToDataLayer,
          actions.updateIsNewUserInContextToFalse,
          actions.callEventsFromConfig
        ]
      },
      [events.onErrorGetOTP]: {
        actions: [
          actions.setNextAndSubmitBtnLoadingToFalse,
          actions.assignChildSelfTransitionToConfigEvents,
          actions.assignErrorMessageToContext
        ]
      },
      [events.doChildMachineSelfTransition]: {
        actions: [actions.doChildMachineSelfTransition]
      },
      [events.onSuccessGetSignUpOTP]: {
        actions: [
          actions.setIsOtpButtonLoadingToFalse,
          actions.setNextAndSubmitBtnLoadingToFalse,
          actions.sendGetOtpCustomEventToDataLayer,
          actions.clearErrorMessageFromContext,
          actions.updateIsOtpSentToUserInContext,
          actions.updateIsNewUserInContextToTrue,
          actions.callEventsFromConfig
        ]
      },
      [events.sendGAEvent]: {
        actions: [actions.sendGAEventToDataLayer, actions.callEventsFromConfig]
      },
      [events.sendFrontendSegmentEvent]: {
        actions: [
          actions.sendFrontendSegmentEvent,
          actions.callEventsFromConfig
        ]
      },
      [events.goToOverRideSubmitFormState]: {
        target: states.overRideFormSubmission
      },
      [events.updateFormValuesToContext]: {
        actions: [actions.updateFormValuesToContext]
      },
      [events.postMessageFormSubmissionSuccess]: {
        actions: [
          actions.sendFormSubmissionSuccessPostMessage,
          actions.callEventsFromConfig
        ]
      },
      [events.postMessageOverRideFormSubmissionSuccess]: {
        actions: [
          actions.sendOverRideFormSubmissionSuccessPostMessage,
          actions.callEventsFromConfig
        ]
      },
      [events.postMessagePublicFormSubmissionSuccess]: {
        actions: [
          actions.sendPublicFormSubmissionSuccessPostMessage,
          actions.callEventsFromConfig
        ]
      },
      [sendPostMessage]: {
        actions: [
          actions.sendPostMessage,
          actions.callEventsFromConfig
        ]
      },
      [events.resetAuthMachine]: {
        actions: [
          assign((context: FormsMachineContext) => {
            return {
              authMachineRef: null,
              isOtpSentToUser: false
            };
          })
        ]
      },
      [goToOtpVerificationStep]: {
        target: states.initializeAuthMachineState
      },
      [getIbFormSubmissionDetails]: {
        target: states.getIbFormSubmissionDetailsState
      }
    },
    states: {
      [loggingOutUser]: {
        tags: [formLoading],
        entry: ['logoutFromSegment'],
        invoke: {
          src: 'logoutUser',
          onDone: [
            {
              cond: 'isProductInfoPresent',
              actions: ['clearErrorMessageFromContext', 'callEventsFromConfig']
            },
            {
              cond: 'shouldLoadScriptLocally',
              actions: 'assignFormConfigLocally',
              target: notLoggedInUserState
            },
            {
              target: getInitialConfigState
            }
          ],
          onError: [
            {
              cond: 'isProductInfoPresent',
              actions: ['clearErrorMessageFromContext', 'callEventsFromConfig']
            },
            {
              target: getInitialConfigState
            }
          ]
        }
      },
      [getInitialConfigState]: {
        tags: [getInitialConfigLoadingTag],
        invoke: {
          src: 'getInitialConfig',
          onDone: [
            {
              target: notLoggedInUserState,
              actions: ['validateDataAndAssignProductConfigToContext']
            }
          ],
          onError: {
            target: getInitialConfigFailureState
          }
        }
      },
      [getInitialConfigFailureState]: {
        tags: [getInitialConfigFailureState],
        on: {
          [getInitialConfigRetryEvent]: getInitialConfigState
        }
      },
      [states.getFormReferralCodeState]: {
        tags: [getInitialConfigLoadingTag],
        invoke: {
          src: 'getFormReferralCode',
          onDone: [
            {
              cond: 'shouldTrackFormReferralAttempt',
              actions: [actions.onSuccessGetFormReferralCode],
              target: states.trackFormReferralAttemptState
            },
            {
              actions: [actions.onSuccessGetFormReferralCode],
              target: loggedInUserState
            }
          ],
          onError: {
            target: loggedInUserState
          }
        }
      },
      [states.trackFormReferralAttemptState]: {
        tags: [getInitialConfigLoadingTag],
        invoke: {
          src: 'trackFormReferralAttempt',
          onDone: {
            target: loggedInUserState
          },
          onError: {
            actions: [actions.onErrorTrackFormReferralAttemptLogEntry],
            target: loggedInUserState
          }
        }
      },
      [loggedInUserState]: {},
      [notLoggedInUserState]: {
        entry: [
          'setNextAndSubmitBtnLoadingToFalse',
          'sendFormVisitEvent',
          'callEventsFromConfig'
        ],
        on: {
          [goToOtpVerificationStep]: {
            target: states.initializeAuthMachineState
          }
        }
      },
      [states.initializeAuthMachineState]: {
        entry: [actions.spawnAuthCoreMachine]
      },
      [getProfileDetailsState]: {
        tags: [otpVerifyBtnLoading],
        entry: [
          'setNextAndSubmitBtnLoadingToTrue',
          'setIsOtpVerifiedInContext'
        ],
        invoke: {
          src: 'getProfileDetails',
          onDone: {
            actions: [
              'validateAndSetUserProfileDetailsToContext',
              'loadSegmentScript',
              'sendIdentifySegmentEvent',
              'sendOTPVerifiedFormAnalytics',
              'clearErrorMessageFromContext'
            ],
            target: getClientAuthCodeState
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [doSelfTransition]: getProfileDetailsState
        }
      },
      [getClientAuthCodeState]: {
        tags: [otpVerifyBtnLoading],
        entry: [
          'setNextAndSubmitBtnLoadingToTrue',
          'resetOTP',
          'sendUserVerifiedOtpEvent'
        ],
        invoke: {
          src: 'getClientAuthCode',
          onDone: {
            actions: 'onSuccessGettingClientAuthCode',
            target: getClientAuthenticationDetailsState
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [doSelfTransition]: getClientAuthCodeState
        }
      },
      [getClientAuthenticationDetailsState]: {
        tags: [otpVerifyBtnLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'getClientAuthenticationDetails',
          onDone: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'onSuccessGettingClientAuthenticationDetails',
              'updateIsLoggedInInContext',
              'callEventsFromConfig'
            ],
            target: states.getFormReferralCodeState
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [doSelfTransition]: getClientAuthenticationDetailsState
        }
      },
      [updateProfileState]: {
        tags: [buttonLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'updateProfile',
          onDone: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'clearErrorMessageFromContext',
              'callEventsFromConfig'
            ]
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [doSelfTransition]: updateProfileState
        }
      },

      [updateGraphQlFieldsState]: {
        tags: [buttonLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'updateGraphQlFields',
          onDone: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'clearErrorMessageFromContext',
              'callEventsFromConfig'
            ]
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [doSelfTransition]: updateGraphQlFieldsState
        }
      },

      [submitForm]: {
        tags: [buttonLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'submitForm',
          onDone: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'updateOrderDetailsInContext',
              'clearErrorMessageFromContext',
              'sendFormSubmitEventToDataLayer',
              'updateFormSubmittedValues',
              actions.sendFormSubmissionSuccessPostMessage,
              'callEventsFromConfig'
            ]
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [doSelfTransition]: submitForm
        }
      },
      [states.overRideFormSubmission]: {
        tags: [buttonLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'overRideFormSubmission',
          onDone: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'clearErrorMessageFromContext',
              'updateFormSubmittedValues',
              actions.sendOverRideFormSubmissionSuccessPostMessage,
              'callEventsFromConfig'
            ]
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [doSelfTransition]: states.overRideFormSubmission
        }
      },
      [submitPublicForm]: {
        tags: [buttonLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'submitPublicForm',
          onDone: {
            actions: [
              'clearErrorMessageFromContext',
              'updateOrderDetailsInContext',
              'sendFormSubmitEventToDataLayer',
              'setNextAndSubmitBtnLoadingToFalse',
              'updateFormSubmittedValues',
              actions.sendPublicFormSubmissionSuccessPostMessage,
              'callEventsFromConfig'
            ]
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [doSelfTransition]: submitPublicForm
        }
      },
      [getCollegePredictorClientAuthCodeState]: {
        tags: [otpVerifyBtnLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue', 'resetOTP'],
        invoke: {
          src: 'getCollegePredictorClientAuthCode',
          onDone: {
            actions: 'onSuccessGettingClientAuthCodeForCollegePredictor',
            target: getCollegePredictorClientAuthenticationDetailsState
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [doSelfTransition]: getCollegePredictorClientAuthCodeState
        }
      },
      [getCollegePredictorClientAuthenticationDetailsState]: {
        tags: [otpVerifyBtnLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'getCollegePredictorClientAuthenticationDetails',
          onDone: {
            actions: [
              'onSuccessGettingClientAuthenticationDetailsForCollegePredictor',
              'callEventsFromConfig'
            ]
          },
          onError: {
            actions: [
              'setNextAndSubmitBtnLoadingToFalse',
              'assignErrorMessageToContext',
              'assignSelfTransitionToConfigEvents'
            ]
          }
        },
        on: {
          [doSelfTransition]:
            getCollegePredictorClientAuthenticationDetailsState
        }
      },

      [states.getIbFormSubmissionDetailsState]: {
        tags: [buttonLoading],
        entry: ['setNextAndSubmitBtnLoadingToTrue'],
        invoke: {
          src: 'getIbFormSubmissionDetails',
          onDone: {
            actions: [
              actions.onSuccessGettingIbFormSubmissionDetails,
              actions.callEventsFromConfig
            ]
          },
          onError: [
            {
              cond: (context, event) => {
                const errorStatus = event?.data && getAPIErrorEnum(event.data);
                if (errorStatus === 'NO_SUBMISSION_DETAILS_FOUND') {
                  return true;
                } else return false;
              },
              actions: [actions.callEventsFromConfig]
            },
            {
              actions: [
                'setNextAndSubmitBtnLoadingToFalse',
                'assignErrorMessageToContext',
                'assignSelfTransitionToConfigEvents'
              ]
            }
          ]
        },
        on: {
          [doSelfTransition]: states.getIbFormSubmissionDetailsState
        }
      }
    }
  });
